import { useCallback, useEffect, useState } from "react";
import { getQuestionByUUID, getQuestionHistoryByUUID } from "../../../services/questionService";

// The question of a question page (with the user's `actions`) and its status history, newest first. `reload()`
// reads both again after a change: the page keeps showing what it has meanwhile. After a failure to read the
// question, it is a retry: loading again, until it fails again. `evaluator` (a user's uuid) adds that user's vote
// to the question. What was read for another question is never shown.
export default function useQuestionPage(uuid, { evaluator } = {}) {
  const [question, setQuestion] = useState(null);
  const [failure, setFailure] = useState({ uuid: null, error: null });
  const [historyOf, setHistoryOf] = useState({ uuid: null, records: [], failed: false });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    getQuestionByUUID({ uuid, evaluator, signal: controller.signal })
      .then((data) => {
        setQuestion(data);
        setFailure({ uuid: null, error: null });
      })
      .catch((error) => {
        if (!controller.signal.aborted) setFailure({ uuid, error });
      });
    getQuestionHistoryByUUID({ uuid, signal: controller.signal })
      .then((data) => setHistoryOf({ uuid, records: Array.isArray(data) ? data : [], failed: false }))
      .catch(() => {
        if (controller.signal.aborted) return;
        setHistoryOf((current) => ({ uuid, records: current.uuid === uuid ? current.records : [], failed: true }));
      });
    return () => controller.abort();
  }, [uuid, evaluator, version]);

  const reload = useCallback(() => {
    setFailure({ uuid: null, error: null });
    setVersion((v) => v + 1);
  }, []);
  const historyRead = historyOf.uuid === uuid;

  return {
    question: question?.uuid === uuid ? question : null,
    loadError: failure.uuid === uuid ? failure.error : null,
    history: historyRead ? historyOf.records : [],
    historyLoading: !historyRead,
    historyFailed: historyRead && historyOf.failed,
    reload,
  };
}
